import { documentService } from '@/services/document.service'
import { useAuthStore } from '@/stores/auth.store'
import type { AddLearnedSkillsRequest } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const documentKeys = {
  all: ['documents'] as const,
  list: () => [...documentKeys.all, 'list'] as const,
}

/**
 * Centralized hook for fetching documents.
 * Uses staleTime to prevent excessive refetches across components.
 * Multiple components can call this - React Query will share the cached data.
 */
export function useDocuments() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  return useQuery({
    queryKey: documentKeys.list(),
    queryFn: () => documentService.listDocuments(),
    enabled: isAuthenticated,
    // Data is considered fresh for 30 seconds - prevents refetch on mount
    staleTime: 30 * 1000,
    // Keep in cache for 5 minutes after unmount
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Get the current (most recent) resume from documents.
 */
export function useCurrentResume() {
  const { data: documents, ...rest } = useDocuments()

  const currentResume = documents
    ?.filter((d) => d.document_type === 'resume')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]

  return { currentResume, documents, ...rest }
}

export function useAddLearnedSkills(documentId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AddLearnedSkillsRequest) =>
      documentService.addLearnedSkills(documentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all })
    },
  })
}
